import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api } from '@/lib/api';
import type { LayoutBlockDefault } from '@/lib/use-page-layout';

/**
 * The company detail page's sections (Tasks and Notes added 2026-10-16) — their ORDER and which are FOLDED —
 * as a per-user preference stored AGAINST THE ACCOUNT.
 *
 * ## 2026-10-20: ORDER moved to the page layout manager; FOLDING stays here
 *
 * Owner's decision (layout manager, 2026-10-20): the company page keeps folding
 * and gains resize and hide through the shared manager (`use-page-layout.ts`,
 * `preferences.layouts.company`). Order, width and hidden now live THERE; this
 * file owns only the FOLDS (`collapsed`).
 *
 * ⚠️ **`order` is still READ and still WRITTEN, but never CHANGED here.** It is
 * the carry-over source: the page builds the manager's `defaults` in this
 * person's stored order, so somebody who arranged the page under the old
 * control sees exactly their page until they edit with the new manager. It is
 * written back unchanged on every fold, because the shape rule below (both
 * keys, as arrays, on every write) is what makes this branch safe without a
 * baseline. There is no `move` any more: ONE way to reorder, the manager's.
 * `reset` still deletes the whole branch (folds AND the carried order) — the
 * page calls it together with the manager's reset, so "Reset" means the
 * built-in layout.
 *
 * Owner's instruction, 2026-10-11: "sections can be arranged per user basis and
 * that to be stored in the preference store." So this is the same class of thing
 * as a table's column layout, and it follows `use-column-layout.ts` wherever the
 * two questions are the same, and says so where they are not.
 *
 * ## Where it lives
 *
 * **`preferences.companyPage = { order: string[], collapsed: string[] }`, a
 * SIBLING of `columns`, `tables` and `enquiryAlerts`.**
 *
 * ⛔ **NEVER inside `preferences.tables`.** That branch is the saved-filter
 * scopes, and `preferences-store` treats every key it finds there as one more
 * filter to delete on the next whole-set save. A layout parked in it would be
 * wiped by the next filter save on some unrelated list page.
 *
 * ⛔ **No `localStorage`, not even as a cache.** Two authorities for one
 * preference means somebody signing in on a second device gets whichever loaded
 * last. There is no browser copy here at all.
 *
 * ## Why this file has NO baseline map and NO reset-on-identity-change
 *
 * `preferences-store` and `use-column-layout` both keep a module-level baseline
 * of the keys the server last held, and send an explicit `null` for every key
 * absent from the next write — because the API merges an OBJECT over an object
 * RECURSIVELY, so a patch naming three of four parts leaves the fourth behind.
 * Both therefore have to be reset at every identity change (sign-out,
 * support-session start, support-session end — see `AuthProvider`).
 *
 * **That hazard does not reach this branch, for two reasons that only work
 * together.** Both of its keys are ARRAYS, and an array REPLACES WHOLESALE
 * rather than merging; and every write here names BOTH keys in one body
 * (`patchBody`). So whatever the server held, after a write it holds exactly
 * `{order, collapsed}` as sent — there is no third key to leave behind and no
 * previous value to merge over. The only other write is `companyPage: null`,
 * which deletes the branch outright.
 *
 * ⚠️ **That property is a consequence of the SHAPE, and it is lost the moment a
 * third key is added without being sent on every write, or either key becomes an
 * object.** If that ever happens, this file needs the baseline-and-reset dance its
 * two neighbours carry, including a call from `AuthProvider`. Until then there is
 * deliberately no module-level state in this file: nothing to forget on sign-out,
 * nothing to hand from one identity to the next. The one gap that leaves is
 * stated rather than engineered around: a company page that stays MOUNTED across
 * a support-session switch keeps the previous identity's layout on screen until
 * it remounts, because the read runs once per mount. The page is re-rendered
 * under a different route on that switch today, so the gap is theoretical.
 *
 * ## The two rules inherited from the column layout, and why they hold here
 *
 * 🔴 **THE COLLAPSED IDS ARE STORED, NEVER THE OPEN ONES.** A stored preference
 * may only ever SUBTRACT. Storing the open set would make every section added
 * later default to folded for everybody who has ever touched the control — and a
 * folded section nobody knows exists reads as the feature being absent. Storing
 * the folded set inverts that: a new section defaults OPEN.
 *
 * 🔴 **THE DECLARED SECTION LIST IS THE AUTHORITY, NOT THE STORED ORDER.** An id
 * the page no longer renders is dropped from a stored order, and an id the page
 * has GAINED lands in its declared position rather than at the end
 * (`applySectionOrder`, mirroring `applyOrder`). A new section appearing last on
 * everybody's page who ever rearranged one is the same silent-subtraction bug.
 *
 * ## Three states on read, and they must not be collapsed
 *
 *   | the branch                                  | means                       |
 *   | ------------------------------------------- | --------------------------- |
 *   | ABSENT (nobody has saved anything)          | the DEFAULTS apply          |
 *   | present, well-formed, `collapsed: []`       | EVERYTHING OPEN — a choice  |
 *   | present and MALFORMED                       | the DEFAULTS apply, quietly |
 *
 * The second row is the one a `?? defaults` would destroy: a person who opened
 * every section and saved would get the four default folds back on every visit.
 * `null` and `[]` are different facts here, as everywhere in this repo. The
 * document is unvalidated by design (a typo'd key is stored and does nothing), so
 * the shape is checked at this border with runtime guards and a malformed branch
 * falls back to defaults — it never throws into a render.
 *
 * ## Writes
 *
 * Local state updates OPTIMISTICALLY and the `PATCH` follows. Writes are sent
 * ONE AT A TIME per hook instance and a change made while one is in the air
 * coalesces into the next — every write carries BOTH whole arrays, so the last
 * to ARRIVE wins, and HTTP promises nothing about that being the last one sent.
 * `keepalive` so a fold followed by F5 is not cancelled by the reload.
 *
 * A failed write KEEPS the local state (it is never rolled back under the
 * reader's hands) and exposes `saveError`, which the page may show quietly. The
 * layout is chrome; a preferences write failing must not make the page look
 * broken. A later successful write clears it.
 *
 * ⚠️ **No change is accepted while the first read is in flight.** Every write
 * carries the WHOLE order and the WHOLE folded set, so a toggle made against the
 * defaults before the stored layout arrived would OVERWRITE the stored layout
 * with defaults-plus-one-change — the reader's arrangement, gone on a click made
 * during a quarter-second window. `toggle` (and `reset`) is a
 * no-op until `status` leaves `'loading'`; the page can render its headers inert
 * for that moment. A FAILED read unlocks the controls: the defaults are in force
 * and an arrangement made over them is saved like any other.
 */

/** The section ids, in their DEFAULT order. The page renders exactly these. */
export const COMPANY_PAGE_SECTIONS = [
  'overview',
  /*
   * Added 2026-10-25 (owner: account lead history, effective-dated). Directly
   * after Overview, FOLDED by default (in `DEFAULT_COLLAPSED_SECTIONS`); a stored
   * layout gets it spliced in after Overview by `applySectionOrder`. Never rename.
   */
  'lead-history',
  'contacts',
  /*
   * Added 2026-10-16 (owner: "add all tasks of previous, outstanding and
   * upcoming on each company details page"). OPEN by default — deliberately
   * absent from `DEFAULT_COLLAPSED_SECTIONS`. A reader with a stored layout
   * gets it spliced in directly after Contacts in THEIR order (see
   * `applySectionOrder`), and open, because only folded ids are stored.
   */
  'tasks',
  /*
   * Added 2026-10-16 (owner: "add a notes section also similar to tasks").
   * Directly after Tasks, OPEN by default — absent from
   * `DEFAULT_COLLAPSED_SECTIONS`. A stored layout gets it spliced in right
   * after Tasks in the reader's own order, by the same rule.
   */
  'notes',
  'history',
  'purchases',
  /*
   * Added 2026-10-18 (owner: a "Services" section — the company's Client
   * Deliverables rows for the current and previous cycle). Directly after
   * Purchases & interest, OPEN by default — absent from
   * `DEFAULT_COLLAPSED_SECTIONS`, so it reads the same for a reader with a
   * stored layout (who gets it spliced in right after Purchases in their own
   * order, and open, because only folded ids are stored) as for one without.
   * The page skips it for a reader without `deliverables` read.
   */
  'services',
  'attachments',
  /*
   * Added 2026-10-23 (BCC email log): the company's logged email threads.
   * Directly after Attachments, OPEN by default — absent from
   * `DEFAULT_COLLAPSED_SECTIONS`, so a reader with a stored layout (who gets it
   * spliced in right after Attachments in their own order, and open, because
   * only folded ids are stored) sees the same as one without.
   */
  'emails',
  'marketing',
  /*
   * Added 2026-10-18 (Product Insights → SEED FAB): the linked school(s)'
   * funnel. Titled "Product Insights" since 2026-10-18 (owner ruling 3: FAB
   * plus every programme with data); the id stays 'fab' because layouts are
   * stored per user. FOLDED by default (in `DEFAULT_COLLAPSED_SECTIONS`). The section
   * renders nothing for a reader without `product-insights` read or a company
   * with no linked school, so most pages never show it.
   */
  'fab',
  /*
   * Added 2026-10-25 (company names and platform mapper, M6): the SEED platform
   * companies linked to this company, with link and unlink. Directly after
   * Product Insights, OPEN by default — absent from `DEFAULT_COLLAPSED_SECTIONS`,
   * so a reader with a stored layout (who gets it spliced in right after `fab`
   * in their own order by `applySectionOrder`, and open, because only folded
   * ids are stored) sees the same as one without.
   */
  'mapping',
  'xero',
] as const;

export type CompanyPageSectionId = (typeof COMPANY_PAGE_SECTIONS)[number];

/** Folded when nothing is stored: Overview, Contacts, Tasks, Notes and History open. */
export const DEFAULT_COLLAPSED_SECTIONS: ReadonlySet<CompanyPageSectionId> = new Set<
  CompanyPageSectionId
>(['lead-history', 'purchases', 'attachments', 'marketing', 'fab', 'xero']);

/**
 * Each section's visible heading — the label the page layout manager shows in
 * Edit layout. Kept beside the ids so a new section cannot ship without one.
 */
export const COMPANY_PAGE_SECTION_LABELS: Readonly<Record<CompanyPageSectionId, string>> = {
  overview: 'Overview',
  'lead-history': 'Account lead history',
  contacts: 'Contacts',
  tasks: 'Tasks',
  notes: 'Notes',
  history: 'Sales & Billing',
  purchases: 'Purchases & interest',
  services: 'Services',
  attachments: 'Attachments',
  emails: 'Emails',
  marketing: 'Marketing emails',
  fab: 'Product Insights',
  mapping: 'Company mapping',
  xero: 'Xero & billing setup',
};

/**
 * The page layout manager's `defaults` for the company page (2026-10-20): every
 * section full width, in the given order — the person's stored `order` from this
 * hook, so an arrangement made under the old control carries over untouched.
 */
export function companyPageLayoutDefaults(
  order: readonly CompanyPageSectionId[],
): LayoutBlockDefault[] {
  return order.map((id) => ({ id, label: COMPANY_PAGE_SECTION_LABELS[id] }));
}

const COMPANY_PAGE_KEY = 'companyPage';

interface SectionLayout {
  order: readonly CompanyPageSectionId[];
  collapsed: ReadonlySet<CompanyPageSectionId>;
}

const DEFAULT_LAYOUT: SectionLayout = {
  order: COMPANY_PAGE_SECTIONS,
  collapsed: DEFAULT_COLLAPSED_SECTIONS,
};

const KNOWN_IDS: ReadonlySet<string> = new Set(COMPANY_PAGE_SECTIONS);

function isSectionId(value: string): value is CompanyPageSectionId {
  return KNOWN_IDS.has(value);
}

/**
 * Order the declared sections by a stored order.
 *
 * `null` or an empty list answers the declared order. Ids the stored list names
 * that the page does not declare are DROPPED; ids the page declares that the
 * stored list never mentioned are spliced in — never appended, for the reason
 * in the header — directly AFTER their nearest declared predecessor as it sits
 * in the reader's own order (so `tasks` lands right after Contacts wherever the
 * reader moved Contacts to; `notes` likewise right after Tasks), or first when
 * no declared predecessor is present.
 *
 * ⚠️ 2026-10-16: this used to splice at the DECLARED INDEX, which put a new
 * section next to whatever happened to occupy that slot in a rearranged page.
 * For a reader who never rearranged anything the two rules agree.
 */
export function applySectionOrder(
  stored: readonly string[] | null,
): readonly CompanyPageSectionId[] {
  if (stored === null || stored.length === 0) return COMPANY_PAGE_SECTIONS;
  const seen = new Set<CompanyPageSectionId>();
  const ordered: CompanyPageSectionId[] = [];
  for (const id of stored) {
    if (isSectionId(id) && !seen.has(id)) {
      ordered.push(id);
      seen.add(id);
    }
  }
  COMPANY_PAGE_SECTIONS.forEach((id, index) => {
    if (seen.has(id)) return;
    // Walk back through the declared list to the nearest section already
    // placed; insert after it. Earlier missing ids were placed in this same
    // loop, so a run of new sections keeps its declared order.
    let at = 0;
    for (let back = index - 1; back >= 0; back -= 1) {
      const predecessor = COMPANY_PAGE_SECTIONS[back];
      const position = predecessor === undefined ? -1 : ordered.indexOf(predecessor);
      if (position !== -1) {
        at = position + 1;
        break;
      }
    }
    ordered.splice(at, 0, id);
    seen.add(id);
  });
  return ordered;
}

/** A string array, or `null` when the value is not an array at all. */
function readStringArray(value: unknown): readonly string[] | null {
  if (!Array.isArray(value)) return null;
  return value.filter((entry): entry is string => typeof entry === 'string');
}

/**
 * The stored branch, read at the border.
 *
 * Absent (`null` / `undefined`) and malformed both answer the defaults, but for
 * different reasons and through different lines, so the distinction stays
 * legible to the next reader. Well-formed means an object carrying BOTH `order`
 * and `collapsed` as arrays — which is the only shape this file ever writes, so
 * anything else was written by something other than this file and is not
 * trusted. Non-string entries inside an array are dropped rather than failing
 * the whole branch; an unknown id is dropped the same way.
 */
function readLayout(branch: unknown): SectionLayout {
  if (branch === null || branch === undefined) return DEFAULT_LAYOUT;
  if (typeof branch !== 'object' || Array.isArray(branch)) return DEFAULT_LAYOUT;
  const record = branch as Record<string, unknown>;
  const order = readStringArray(record.order);
  const collapsed = readStringArray(record.collapsed);
  if (order === null || collapsed === null) return DEFAULT_LAYOUT;
  return {
    order: applySectionOrder(order),
    // `[]` is a real answer — everything open — and must survive as an empty set.
    collapsed: new Set(collapsed.filter(isSectionId)),
  };
}

function branchOf(preferences: unknown): unknown {
  if (preferences === null || typeof preferences !== 'object') return null;
  return (preferences as Record<string, unknown>)[COMPANY_PAGE_KEY] ?? null;
}

function sameOrder(a: readonly CompanyPageSectionId[], b: readonly CompanyPageSectionId[]): boolean {
  return a.length === b.length && a.every((id, index) => b[index] === id);
}

function sameSet(a: ReadonlySet<CompanyPageSectionId>, b: ReadonlySet<CompanyPageSectionId>): boolean {
  if (a.size !== b.size) return false;
  for (const id of a) if (!b.has(id)) return false;
  return true;
}

function isDefaultLayout(layout: SectionLayout): boolean {
  return (
    sameOrder(layout.order, DEFAULT_LAYOUT.order) &&
    sameSet(layout.collapsed, DEFAULT_LAYOUT.collapsed)
  );
}

/**
 * The PATCH body. BOTH keys, always, as arrays — see the header for why that is
 * what makes a baseline unnecessary. `null` for the whole branch deletes it,
 * which is what "reset" means: nothing left behind, so a later default change
 * reaches this reader too.
 */
function patchBody(layout: SectionLayout | null) {
  const branch =
    layout === null
      ? null
      : { order: [...layout.order], collapsed: [...layout.collapsed] };
  return { preferences: { [COMPANY_PAGE_KEY]: branch } };
}

const SAVE_FAILED = 'The section layout could not be saved. It still applies here for now.';

function saveErrorOf(error: unknown): string {
  return error instanceof Error && error.message.trim().length > 0 ? error.message : SAVE_FAILED;
}

/** A queued write: a layout to store, or `null` meaning delete the branch. */
interface PendingWrite {
  layout: SectionLayout | null;
}

export interface CompanyPageLayout {
  /**
   * The person's STORED order (declared order when nothing is stored), read
   * only — the carry-over source for the page layout manager's defaults. Never
   * changed by this hook (2026-10-20); see the header.
   */
  order: readonly CompanyPageSectionId[];
  isCollapsed: (id: CompanyPageSectionId) => boolean;
  /** Fold or unfold one section. A no-op while `status === 'loading'`. */
  toggle: (id: CompanyPageSectionId) => void;
  /** Back to the defaults, and delete the stored branch (folds and carried order). */
  reset: () => void;
  /** True when both the order and the folded set equal the defaults. */
  isDefault: boolean;
  /**
   * `'failed'` means the READ failed: the defaults are in force and writes are
   * still attempted, exactly as if nothing had been stored.
   */
  status: 'loading' | 'ready' | 'failed';
  /** The last write's failure, or `null`. Cleared by the next successful write. */
  saveError: string | null;
}

export function useCompanyPageLayout(): CompanyPageLayout {
  const [layout, setLayout] = useState<SectionLayout>(DEFAULT_LAYOUT);
  const [status, setStatus] = useState<'loading' | 'ready' | 'failed'>('loading');
  const [saveError, setSaveError] = useState<string | null>(null);

  // Mirrors `status !== 'loading'` for use inside callbacks and updaters, where
  // reading state would be a render behind.
  const loaded = useRef(false);
  // Bumped on every local change, so a late response can tell it was overtaken.
  const generation = useRef(0);
  const pending = useRef<PendingWrite | null>(null);
  const running = useRef(false);
  // The write queue must keep draining after this hook unmounts (a fold
  // followed by navigating away still has to land), so it only ever sets state
  // while mounted.
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;
    api
      .getPreferences(signal)
      .then((response) => {
        if (signal.aborted) return;
        setLayout(readLayout(branchOf(response.data.preferences)));
        loaded.current = true;
        setStatus('ready');
      })
      .catch(() => {
        if (signal.aborted) return;
        /*
         * ⚠️ **A FAILED READ DEGRADES TO THE DEFAULTS AND NEVER BLOCKS THE
         * PAGE.** The record is the page; the arrangement is chrome. The
         * controls are unlocked anyway: the reader may arrange the defaults, and
         * that arrangement is saved like any other.
         */
        setLayout(DEFAULT_LAYOUT);
        loaded.current = true;
        setStatus('failed');
      });
    return () => controller.abort();
  }, []);

  const drain = useCallback(async () => {
    if (running.current) return;
    running.current = true;
    try {
      while (pending.current !== null) {
        const write = pending.current;
        pending.current = null;
        const sentAt = generation.current;
        try {
          const response = await api.updatePreferences(patchBody(write.layout), undefined, true);
          if (!mounted.current) continue;
          setSaveError(null);
          /*
           * Render from what came back, not from what was sent — the standing
           * rule for this document. The two agree whenever the server stored
           * the arrays as sent, so this is a no-op in the ordinary case; it only
           * moves anything if the server held something other than what was
           * written. A change made while this write was in the air outranks the
           * response, which describes a layout the reader has since moved past.
           */
          if (pending.current === null && generation.current === sentAt) {
            setLayout(readLayout(branchOf(response.data.preferences)));
          }
        } catch (error) {
          // Keep the local state; say so quietly. A newer queued write, if any,
          // is the retry.
          if (mounted.current) setSaveError(saveErrorOf(error));
        }
      }
    } finally {
      running.current = false;
    }
  }, []);

  const persist = useCallback(
    (next: SectionLayout | null) => {
      generation.current += 1;
      pending.current = { layout: next };
      void drain();
    },
    [drain],
  );

  const update = useCallback(
    (change: (current: SectionLayout) => SectionLayout) => {
      if (!loaded.current) return;
      setLayout((current) => {
        const next = change(current);
        // A refusal is spelled `return current`; identity means nothing to write.
        if (next !== current) persist(next);
        return next;
      });
    },
    [persist],
  );

  const isCollapsed = useCallback(
    (id: CompanyPageSectionId) => layout.collapsed.has(id),
    [layout.collapsed],
  );

  const toggle = useCallback(
    (id: CompanyPageSectionId) => {
      update((current) => {
        const collapsed = new Set(current.collapsed);
        if (!collapsed.delete(id)) collapsed.add(id);
        return { ...current, collapsed };
      });
    },
    [update],
  );

  /*
   * ⛔ No `move` / `canMove` since 2026-10-20: the page layout manager owns the
   * order. `order` rides along unchanged inside `toggle`'s `...current`.
   */

  const reset = useCallback(() => {
    if (!loaded.current) return;
    setLayout((current) => {
      // Already the defaults: nothing to delete, and a `PATCH` that deletes a
      // branch that is not there is a request nobody can explain.
      if (isDefaultLayout(current)) return current;
      persist(null);
      return DEFAULT_LAYOUT;
    });
  }, [persist]);

  const isDefault = useMemo(() => isDefaultLayout(layout), [layout]);

  /*
   * Memoised so a caller may depend on the controller itself — the lesson
   * `useColumnLayout` records after six pages worked around a fresh identity per
   * render. Every member is already stable underneath.
   */
  return useMemo(
    () => ({
      order: layout.order,
      isCollapsed,
      toggle,
      reset,
      isDefault,
      status,
      saveError,
    }),
    [layout.order, isCollapsed, toggle, reset, isDefault, status, saveError],
  );
}
